import React, { createContext, useContext, useMemo } from 'react';

import { loadOnboarding, reloadOnboarding, upsertOnboarding } from 'common/actions/onboarding';
import asyncConnect from 'common/core/asyncConnect';

import type { Onboarding } from 'common/api/endpoints/onboarding';
import type { AsyncItem, State } from 'redux-connect';

export type OnboardingContextType = {
  onboarding: Onboarding;
  upsertOnboarding: (data: Onboarding) => void;
  reloadOnboarding: () => void;
};

export const OnboardingContext = createContext<OnboardingContextType | null>(null);

type OwnProps = OnboardingContextType & {
  children: React.ReactNode;
};

const OnboardingContainer = ({
  onboarding,
  upsertOnboarding,
  reloadOnboarding,
  children,
}: OwnProps) => {
  const memoedContext = useMemo(
    () => ({ onboarding, upsertOnboarding, reloadOnboarding }),
    [onboarding, upsertOnboarding, reloadOnboarding],
  );

  return <OnboardingContext.Provider value={memoedContext}>{children}</OnboardingContext.Provider>;
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within an OnboardingContainer');
  }
  return context;
};

export const asyncFetch: AsyncItem = {
  promise: ({ store: { dispatch } }) => {
    return dispatch(loadOnboarding());
  },
};

export default asyncConnect(
  [asyncFetch],
  (state: State) => ({ onboarding: state.onboarding.data }),
  (dispatch) => ({
    upsertOnboarding: (data: Onboarding) => dispatch(upsertOnboarding(data)),
    reloadOnboarding: () => dispatch(reloadOnboarding()),
  }),
)(OnboardingContainer);
